import {useContext, useMemo, useState} from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  IconButton,
  Stack,
  Text,
  Textarea,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {FiSend, FiTrash2, FiType} from "react-icons/fi";
import {AdapterContext, ConnectionContext} from "../Contexts";

export function encodeUtf16BigEndian(value) {
  const bytes = [];
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    bytes.push((code >> 8) & 0xff, code & 0xff);
  }
  return bytes;
}

export function TextMode() {
  const adapter = useContext(AdapterContext);
  const connected = useContext(ConnectionContext);
  const toast = useToast();
  const maxLength = adapter.capabilities.text?.maxLength ?? 50;
  const [value, setValue] = useState(() => localStorage.getItem(`text-draft:${adapter.id}`) ?? localStorage.getItem("text") ?? "");
  const [sending, setSending] = useState(false);
  const normalizedValue = useMemo(() => value.replace(/\n/g, ""), [value]);

  const send = async (text = normalizedValue) => {
    setSending(true);
    try {
      await adapter.showText(encodeUtf16BigEndian(text));
      toast({title: text ? "Text sent" : "Text cleared", status: "success", duration: 1800});
    } catch (error) {
      toast({title: "Send failed", description: error.message, status: "error", duration: 4000});
    } finally {
      setSending(false);
    }
  };

  return (
    <Grid templateColumns={{base: "1fr", lg: "minmax(0, 1fr) minmax(320px, .8fr)"}} gap={{base: 6, lg: 10}}>
      <Stack spacing={4}>
        <Flex align="center" justify="space-between">
          <HStack color="#777d85">
            <FiType />
            <Text fontSize="11px" textTransform="uppercase" fontWeight="800">Message</Text>
          </HStack>
          <Badge colorScheme={normalizedValue.length >= maxLength ? "orange" : "gray"}>
            {normalizedValue.length} / {maxLength}
          </Badge>
        </Flex>
        <Textarea
          value={value}
          onChange={(event) => {
            const next = event.target.value.replace(/\n/g, "").slice(0, maxLength);
            setValue(next);
            localStorage.setItem(`text-draft:${adapter.id}`, next);
          }}
          maxLength={maxLength}
          minH={{base: "200px", md: "260px"}}
          resize="vertical"
          fontSize={{base: "lg", md: "xl"}}
          bg="#101214"
          borderColor="#30343a"
          borderRadius="5px"
          _focusVisible={{borderColor: "#5ed5c8", boxShadow: "0 0 0 1px #5ed5c8"}}
          placeholder="Type a message"
        />
        <Flex justify="space-between" gap={3} wrap="wrap">
          <Tooltip label="Clear message" hasArrow>
            <IconButton
              aria-label="Clear message"
              icon={<FiTrash2 />}
              variant="ghost"
              borderRadius="5px"
              onClick={async () => {
                setValue("");
                localStorage.setItem(`text-draft:${adapter.id}`, "");
                if (connected) await send("");
              }}
            />
          </Tooltip>
          <Button
            leftIcon={<FiSend />}
            colorScheme="studio"
            color="#17130a"
            borderRadius="5px"
            onClick={() => send()}
            isLoading={sending}
            isDisabled={!connected}
          >
            Send to display
          </Button>
        </Flex>
      </Stack>

      <Flex
        minH="260px"
        align="center"
        justify="center"
        position="relative"
        bg="#070809"
        border="1px solid"
        borderColor="#35393e"
        borderRadius="5px"
        overflow="hidden"
        p={8}
      >
        <Text position="absolute" top={4} left={4} color="#5e646c" fontSize="9px" fontWeight="800" textTransform="uppercase">
          Display preview
        </Text>
        <Box width="100%" overflow="hidden">
          <Text
            color="#ffd84d"
            fontFamily="mono"
            fontSize={{base: "2xl", md: "4xl"}}
            lineHeight="1.1"
            textAlign="center"
            textShadow="0 0 18px rgba(255,216,77,.42)"
            overflowWrap="anywhere"
          >
            {normalizedValue || "..."}
          </Text>
        </Box>
      </Flex>
    </Grid>
  );
}
